import React, {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
// import { AgSparklineOptions } from "ag-charts-community";
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz, colorSchemeLight } from 'ag-grid-community';
import { ToggleWrapOuter, ToggleSlide } from './BudgetTableCss';
import GroupEditModal from './form/GroupEditModal';
// import Notify from '../../../../shared/components/widgets/Notify';
import config from '../../../config';
import Panel from '../../../shared/components/Panel';
import { PLATFORM_BUDGET_TYPES } from '../shared/constants';
// import Slider from '../../../../shared/components/range-slider/Slider';
import {
  currencyFormatter, formatDecimalToFixedTwoPercent, formatNumberOnePlaceDefaultToZero, formatNumberDefaultToZero, getExportPrefix, // formatDecimalToFixedTwoPercentDirect,
} from '../../../shared/components/table/functions';
import { getPlatform } from '../../../shared/components/domainSupport';
import { createSparklineCellRendererParams } from '../../../shared/components/table/sparklineHelpers';

const apiUrl = config.isProdEnv ? config.serverProdUrl : config.serverDevUrl;

export default function BudgetTable({
  allById, internalById, allByDate, internalByDate, retailer, title, allGraphLoad, internalGraphLoad, gridTheme, reportDays, forceRefresh, setForceRefresh, budgetAccountData, activeDayOptions, retailerConfig,
}) {
  const gridRef = useRef();
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [combinedItems, setCombinedItems] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [panelLoad, setPanelLoad] = useState(false);
  const [hideNonEssentialBudget, setHideNonEssentialBudget] = useState(true);
  const [hideNonEssentialSpendControls, setHideNonEssentialSpendControls] = useState(false);
  const [hideNonEssentialMetrics, setHideNonEssentialMetrics] = useState(true);
  const [hideNonEssentialAdvancedMetrics, setHideNonEssentialAdvancedMetrics] = useState(false);
  const [hideNonEssentialSparklines, setHideNonEssentialSparklines] = useState(true);
  const [hideIndividualAllocations, setHideIndividualAllocations] = useState(false);
  const [adminView, setAdminView] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRowData, setEditRowData] = useState({});
  const [disableButton, setDisableButton] = useState(false);

  const platfromSpec = getPlatform();
  const unassignedText = '*UNASSIGNED*';

  const getData = () => {
    const retailerPath = `${retailer.toLowerCase()}/`;
    const budgetUrl = `${apiUrl}/accounts/${retailerPath}budget-group-daily?accountId=${sellerSelect.value}&platform=${platfromSpec.nameKey}`;

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    setPanelLoad(true);
    setBudgetItems([]);

    fetch(budgetUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then((data) => {
        // ROLLUP LOGIC: Remove deduplicate budget_group_name by combining records
        // For ITEM budget_type rows, there can be duplicates that need to be combined
        const groupedByName = {};

        data.budgetGroupData.forEach(item => {
          const groupName = item.budget_group_name;

          if (!groupedByName[groupName]) {
            // First occurrence - store as-is
            groupedByName[groupName] = { ...item };
          } else {
            // Duplicate found - combine ad_type_csv, ad_format, and budget_group_daily_id_csv
            const existing = groupedByName[groupName];

            // Combine budget_group_daily_id_csv (preserve all IDs for saving)
            const existingIds = existing.budget_group_daily_id_csv ? existing.budget_group_daily_id_csv.split(',').map(s => s.trim()) : [];
            const newIds = item.budget_group_daily_id_csv ? item.budget_group_daily_id_csv.split(',').map(s => s.trim()) : [];
            const combinedIds = [...existingIds, ...newIds];
            existing.budget_group_daily_id_csv = combinedIds.join(',');

            // Combine ad_type_csv (remove duplicates)
            const existingAdTypes = existing.ad_type_csv ? existing.ad_type_csv.split(',').map(s => s.trim()) : [];
            const newAdTypes = item.ad_type_csv ? item.ad_type_csv.split(',').map(s => s.trim()) : [];
            const combinedAdTypes = [...new Set([...existingAdTypes, ...newAdTypes])];
            existing.ad_type_csv = combinedAdTypes.join(',');

            // Combine ad_format (remove duplicates)
            const existingFormats = existing.ad_format ? existing.ad_format.split(',').map(s => s.trim()) : [];
            const newFormats = item.ad_format ? item.ad_format.split(',').map(s => s.trim()) : [];
            const combinedFormats = [...new Set([...existingFormats, ...newFormats])];
            existing.ad_format = combinedFormats.join(',');

            // budget_json: keep the existing one (they all have original data)
            // All other fields should be the same, so no action needed
          }
        });

        // Convert back to array
        const deduplicatedData = Object.values(groupedByName);

        // Transform CSV strings into structured budgetGroupRecords array
        const transformedData = deduplicatedData.map(item => {
          const ids = item.budget_group_daily_id_csv ? item.budget_group_daily_id_csv.split(',').map(id => parseInt(id.trim(), 10)) : [];

          // Check if this is an "Other" budget type (External/Manual ADs)
          // Note: "Other" budget type is used for tracking external/manual ad spend
          // Supports all ad types: SP, SD, SB, ST, and DSP
          const isOtherBudgetType = item.budget_type && item.budget_type.toLowerCase() === 'other';

          // Parse ad_type_csv - PRESERVE THE ORIGINAL ORDER from the API!
          // The API order matches the budget_group_daily_id_csv order, so we must keep it intact
          const rawAdTypes = item.ad_type_csv ? item.ad_type_csv.split(',').map(at => at.trim()) : [];

          // Check for DSP first (DSP budgets are standalone)
          const hasDSP = rawAdTypes.includes('DSP');

          // Parse allocation percentages from budget_json if available
          let allocations = [100, 0, 0, 0]; // Default: 100% SP,SD, or 100% DSP for DSP budgets
          try {
            const budgetJson = JSON.parse(item.budget_json || '{}');
            if (budgetJson.allocation?.percentages) {
              if (hasDSP) {
                // For DSP budgets, only dsp_percent matters
                allocations = [budgetJson.allocation.percentages.dsp_percent || 100];
              } else {
                allocations = [
                  budgetJson.allocation.percentages.sp_sd_percent || 0,
                  budgetJson.allocation.percentages.sb_percent || 0,
                  budgetJson.allocation.percentages.st_percent || 0,
                ];
              }
            }
          } catch (e) {
            // Keep defaults
          }

          // Build structured records array - map each ad type to its ID and allocation
          const budgetGroupRecords = [];

          if (hasDSP) {
            // DSP budget - single record
            budgetGroupRecords.push({
              budget_group_daily_id: ids[0] || 0,
              ad_type: 'DSP',
              allocation_percent: allocations[0] || 100,
            });
          } else if (isOtherBudgetType) {
            // Other budget type - use raw ad types in original API order
            // This preserves the order that matches budget_group_daily_id_csv
            rawAdTypes.forEach((adType, index) => {
              budgetGroupRecords.push({
                budget_group_daily_id: ids[index] || 0,
                ad_type: adType,
                allocation_percent: 100, // External budgets are always 100% for their ad type
              });
            });
          } else {
            // Non-DSP, non-Other budgets - standard order
            // IMPORTANT: SP and SD are ALWAYS grouped together and share one ID
            // Example: ad_type_csv="SP,SD,SB" with budget_group_daily_id_csv="359,486"
            // means: SP,SD share ID 359, and SB has ID 486
            const adTypeToId = {};
            let idIndex = 0;

            for (let i = 0; i < rawAdTypes.length; i += 1) {
              const adType = rawAdTypes[i];

              // Check if this is SP or SD - they're grouped together
              if (adType === 'SP' || adType === 'SD') {
                const currentId = ids[idIndex] || 0;

                // Check if the next ad type is also SP or SD (consecutive pairing)
                if (i + 1 < rawAdTypes.length && (rawAdTypes[i + 1] === 'SP' || rawAdTypes[i + 1] === 'SD')) {
                  // Both SP and SD are present - they share the same ID
                  adTypeToId[adType] = currentId;
                  adTypeToId[rawAdTypes[i + 1]] = currentId;
                  i += 1; // Skip the next one since we processed it
                } else {
                  // Only one of SP or SD is present
                  adTypeToId[adType] = currentId;
                }
                idIndex += 1; // Move to next ID after processing SP,SD group
              } else {
                // SB, ST, or other ad types - each gets its own ID
                adTypeToId[adType] = ids[idIndex] || 0;
                idIndex += 1;
              }
            }

            const standardOrder = ['SP,SD', 'SB', 'ST'];

            standardOrder.forEach((stdAdType, stdIndex) => {
              // Find matching ad type in the response
              let id = 0;

              if (stdAdType === 'SP,SD') {
                // Use SP's ID if available, otherwise SD's ID
                if (adTypeToId.SP !== undefined) {
                  id = adTypeToId.SP;
                } else if (adTypeToId.SD !== undefined) {
                  id = adTypeToId.SD;
                }
              } else {
                // Direct lookup for SB, ST
                id = adTypeToId[stdAdType] || 0;
              }

              budgetGroupRecords.push({
                budget_group_daily_id: id,
                ad_type: stdAdType,
                allocation_percent: allocations[stdIndex],
              });
            });
          }

          return {
            ...item,
            budgetGroupRecords, // Add structured array
            // Keep originals for backwards compatibility during transition
          };
        });

        setPanelLoad(false);
        setBudgetItems(transformedData);
      }).catch(() => {
        setPanelLoad(false);
      });
  };

  useEffect(() => {
    getData();
  }, [sellerSelect.value, forceRefresh, retailer]);

  // useEffect(() => {
  //   const newMoPacos = ((pacosNonBrandAvg / 100) + (pacosBrandAvg / 100)) / 2;
  //   setMonthlyPacos(newMoPacos || 0);
  // }, [pacosBrandAvg, pacosNonBrandAvg]);

  useEffect(() => {
    if (!gridRef.current?.api) return;

    // If you're using value cache elsewhere, also do:
    // gridRef.current.api.expireValueCache?.();

    gridRef.current.api.refreshCells({
      columns: ['mtd_ppc_spend', 'mtd_ppc_sales', 'mtd_ppc_acos', 'mtd_ppc_roas'],
      force: true,
    });

    // Recompute group/footer aggregations so the footer uses fresh sums
    gridRef.current.api.refreshClientSideRowModel('aggregate');
  }, [combinedItems]); // or any state that changes the underlying row values/includes


  useEffect(() => {
    if (allById && internalById && allByDate && internalByDate) {
      const newBudgetItems = [];
      const getName = (currentName) => {
        if (currentName === '') {
          return unassignedText;
        }
        return currentName;
      }

      budgetItems?.forEach(budgetItemRow => {
        if (budgetItemRow.budget_group_name || budgetItemRow.budget_group_name === '') {
          const allSpendChange = [];
          const allSalesChange = [];
          const allAcosChange = [];
          const internalSpendChange = [];
          const internalSalesChange = [];
          const internalAcosChange = [];
          // Build Change Sparkline
          allByDate?.forEach(allDate => {
            allSpendChange.push(allDate[`spend_${budgetItemRow.budget_group_name}`]);
            allSalesChange.push(allDate[`sales_${budgetItemRow.budget_group_name}`]);
            allAcosChange.push((allDate[`spend_${budgetItemRow.budget_group_name}`] && allDate[`sales_${budgetItemRow.budget_group_name}`] && allDate[`sales_${budgetItemRow.budget_group_name}`] > 0) ? (allDate[`spend_${budgetItemRow.budget_group_name}`] / allDate[`sales_${budgetItemRow.budget_group_name}`]) : null);
          });
          internalByDate?.forEach(internalDate => {
            internalSpendChange.push(internalDate[`internal_spend_${budgetItemRow.budget_group_name}`]);
            internalSalesChange.push(internalDate[`internal_sales_${budgetItemRow.budget_group_name}`]);
            internalAcosChange.push((internalDate[`internal_spend_${budgetItemRow.budget_group_name}`] && internalDate[`internal_sales_${budgetItemRow.budget_group_name}`] && internalDate[`internal_sales_${budgetItemRow.budget_group_name}`] > 0) ? (internalDate[`internal_spend_${budgetItemRow.budget_group_name}`] / internalDate[`internal_sales_${budgetItemRow.budget_group_name}`]) : null);
          });
          const allMetricRow = allById?.group?.[budgetItemRow.budget_group_name] || {};
          const internalMetricRow = internalById?.group?.[budgetItemRow.budget_group_name] || {};

          // IMPORTANT: Preserve budgetGroupRecords for master-detail functionality
          newBudgetItems.push({
            ...budgetItemRow,
            name: getName(budgetItemRow.budget_group_name),
            ...allMetricRow,
            ...internalMetricRow,
            allSpendChange,
            allSalesChange,
            allAcosChange,
            internalSpendChange,
            internalSalesChange,
            internalAcosChange,
            // Explicitly preserve budgetGroupRecords (in case spreading overwrites it)
            budgetGroupRecords: budgetItemRow.budgetGroupRecords,
          });
        }
      });
      setCombinedItems(newBudgetItems);
    }
  }, [allById, internalById, allByDate, internalByDate, budgetItems]);

  const onGridReady = useCallback(() => {
    // AG Grid v35: Auto-size all columns on initial load
    gridRef.current.api.autoSizeAllColumns();
  }, []);

  const readableActiveDays = (activeDaysCsv) => {
    const readableArray = [];
    const activeDaysArray = activeDaysCsv?.split(',') || [];
    activeDaysArray.forEach((day, pos) => {
      if (day === '1') {
        readableArray.push(moment().isoWeekday(pos + 1).format('dddd'));
      }
    });
    return readableArray.join(', ');
  }

  // const updateBudgetGroupDropdown = (selectedOption, e, itemName) => {
  //   const budgetItemsUpdates = [...budgetItems]; // clone array
  //   const { name: groupName } = e;

  //   const objIndex = budgetItemsUpdates.findIndex((obj => obj.name === groupName));

  //   budgetItemsUpdates[objIndex][itemName] = selectedOption.value;
  //   setBudgetItems(budgetItemsUpdates);
  // }

  // const updateBudgetGroup = (e) => {
  //   const budgetItemsUpdates = [...budgetItems]; // clone array
  //   const { name, value, dataset } = e.target;
  //   const { groupName } = dataset;
  //   const objIndex = budgetItemsUpdates.findIndex((obj => obj.name === groupName));
  //   const itemName = name;
  //   const regex = name === 'portfolioIdsCsv' ? /[^\-0-9.,]/g : /[^\-0-9.]/g;
  //   budgetItemsUpdates[objIndex][itemName] = ['pacos', 'tacos', 'pacosBrand', 'pacosNonBrand'].includes(name) ? (value.replace(regex, '') / 100) : value.replace(regex, '');
  //   setBudgetItems(budgetItemsUpdates);
  // }

  const budgetStateOptions = [
    { label: 'Enabled', value: 'enabled' },
    { label: 'Disabled', value: 'disabled' },
    { label: 'Draft', value: 'draft' },
    { label: 'Completed', value: 'completed' },
  ];

  const limitlessBudgetOptions = [
    { label: 'Strict Budget', value: 0 },
    { label: 'No Budget', value: 1 },
    { label: 'Flexible Budget', value: 2 },
  ];

  const includesOptions = [
    { label: 'All ADs', value: '' },
    { label: 'Limitless ADs', value: 'limitless' },
    { label: 'Manual ADs', value: 'external' },
  ];

  // Gender map for DSP budgets (DSP budgets use includes field to store gender targeting)
  const genderMap = {
    male_and_female: 'Male and Female',
    male: 'Male',
    female: 'Female',
  };

  const spendPriorityOptions = [
    { label: 'Most Brand', value: '-2' },
    { label: 'More Brand', value: '-1' },
    { label: 'Brand and NonBrand', value: '0' },
    { label: 'More NonBrand', value: '1' },
    { label: 'Most NonBrand', value: '2' },
  ];

  const spendTacticOptions = [
    { label: 'Max Performance', value: 'max_perf' },
    { label: 'Full Budget Spend', value: 'full_spend' },
  ];

  const spendSpeedOptions = [
    { label: 'Standard', value: 'speed_level_1' },
    { label: 'Fast', value: 'speed_level_2' },
    { label: 'Fastest', value: 'speed_level_3' },
  ];

  const itemTypeOptions = [
    { label: 'SKU', value: 'SKU' },
    { label: 'ASIN', value: 'ASIN' },
    { label: 'ITEM', value: 'ITEM' },
  ];

  const itemSelectionOptions = [
    { label: 'All', value: 'ALL' },
    { label: 'Top', value: 'TOP' },
  ];

  const destinationOptions = [
    { label: 'On', value: 'ON' },
    { label: 'Off', value: 'OFF' },
  ];

  const setupFlowOptions = [
    { label: 'Quick Setup', value: 'quick' },
    { label: 'Advanced Setup', value: 'advanced' },
  ];

  const agencyInternalOptions = [
    { label: 'Agency View', value: false },
    { label: 'Limitless View', value: true },
  ];

  const budgetDriverOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Monthly', value: 'monthly' },
  ];

  const costTargetTypeOptions = [
    { label: 'Blended', value: 'blended' },
    { label: 'Brand/NonBrand', value: 'bnb' },
  ];

  // const findOption = (value, options) => {
  //   const foundObject = options.find(item => item.value === value);
  //   if (foundObject) {
  //     return foundObject;
  //   }
  //   return options[0];
  // };

  // function extractValues(mappings) {
  //   return mappings.map(map => map.value);
  // }

  function lookupValue(mappings, valueKey) {
    return mappings.find(map => map.value === valueKey);
  }

  const aggFuncs = useMemo(() => {
    const aggFuncsReturn = {
      customAvg: params => {
        let count = 0;
        let total = 0;
        params.values.forEach(value => {
          // if (value > 0) {
          count += 1;
          total += value;
          // }
        });
        const returnValue = count > 0 ? (total / count) : 0;
        return returnValue.toFixed(1);
      },
    };
    return aggFuncsReturn;
  }, []);

  const changeShowEditModal = (data) => {
    setEditRowData(data);
    setShowEditModal(!showEditModal);
  };

  const countActiveDaysInMonth = (activeDaysCsv, startDate, endDate) => {
    let count = 0;
    const today = moment();
    const daysInMonth = today.daysInMonth();
    for (let day = 1; day <= daysInMonth; day += 1) {
      const checkDay = moment([today.year(), today.month(), day]);
      const dayOfWeekNum = checkDay.isoWeekday() - 1;
      if (activeDaysCsv.split(',')[dayOfWeekNum] === '1' && checkDay >= moment(startDate) && (checkDay <= moment(endDate) || endDate === null)) {
        count += 1;
      }
    }
    return count;
  }

  // Get platform-specific default budget type
  const platformKey = retailerConfig?.key || 'amazon';
  const platformBudgetConfig = PLATFORM_BUDGET_TYPES[platformKey];

  let defaultBudgetType;
  if (platformBudgetConfig && platformBudgetConfig.options && platformBudgetConfig.options.length > 0) {
    // Use first option from platform-specific config
    const firstOption = platformBudgetConfig.options[0];
    defaultBudgetType = { label: firstOption.label, value: firstOption.value };
  } else {
    // Fallback to Item type for Amazon or unknown platforms
    defaultBudgetType = { label: 'Item', value: 'item' };
  }

  const addNewDefault = {
    setupFlow: setupFlowOptions[0],
    agencyInternal: agencyInternalOptions[0],
    budgetDriver: budgetDriverOptions[0],
    costTargetType: costTargetTypeOptions[0],
    costTargetMetric: 'roas', // Always ROAS
    budgetType: defaultBudgetType, // Platform-specific default
    spendPriority: spendPriorityOptions[2], // Brand and NonBrand (value: '0')
    startDate: moment(), // Today (can start today, not in past)
    state: { label: 'Enabled', value: 'enabled' }, // Enabled state
    // Active days - all days on by default
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
    // Additional defaults for advanced fields
    includes: includesOptions[1], // Limitless ADs
    spendSpeed: spendSpeedOptions[0], // Standard
    spendTactic: spendTacticOptions[0], // Max Performance
    limitlessBudget: limitlessBudgetOptions[0], // Strict Budget
    itemType: sellerSelect.type === 1 ? itemTypeOptions[0] : itemTypeOptions[1], // SKU for type 1, ASIN for type 2
    itemSelection: itemSelectionOptions[0], // All
    destination: destinationOptions[0], // On
    // DSP-specific defaults
    dspGender: { label: 'Male and Female', value: 'male_and_female' },
    dspAdFormat: { label: 'Display', value: 'display' },
    dspSpendTactic: { label: 'ALL - Full Funnel', value: 'all_full_funnel' },
    dspSpendSpeed: { label: 'Auto (ALL)', value: 'auto_all' },
    dspItemType: { label: 'Product Page', value: 'product_page' },
  };

  // MASTER-DETAIL: Check if row has detail rows (multiple AD types with allocation > 0)
  const isRowMaster = useCallback((dataItem) => {
    // Master-detail only applies to Amazon platform (allocation across multiple ad types)
    if (retailerConfig?.key !== 'amazon') {
      return false;
    }

    if (!dataItem || !dataItem.budgetGroupRecords) {
      return false;
    }

    // Count how many AD types have allocation > 0
    const activeAllocations = dataItem.budgetGroupRecords.filter(
      record => record.allocation_percent > 0,
    );

    // If more than 1 AD type has allocation, this is a master row
    return activeAllocations.length > 1;
  }, [retailerConfig]);

  // MASTER-DETAIL: Detail cell renderer for AD Type rows
  const detailCellRenderer = useMemo(() => (props) => {
    const { data } = props;

    // Filter to only active AD types (allocation > 0)
    const detailRowData = data.budgetGroupRecords
      .filter(record => record.allocation_percent > 0)
      .map(record => {
        const allMetrics = allById?.group?.[data.budget_group_name] || {};
        const internalMetrics = internalById?.group?.[data.budget_group_name] || {};

        // Try to get AD type-specific data from API first
        const adTypeKey = record.ad_type;
        const allByAdType = allMetrics.by_ad_type?.[adTypeKey];
        const internalByAdType = internalMetrics.by_ad_type?.[adTypeKey];

        // Use real AD type-specific data if available, otherwise fall back to zeros
        let allSpend = 0;
        let allSales = 0;
        let internalSpend = 0;
        let internalSales = 0;
        let externalSpend = 0;
        let externalSales = 0;

        if (allByAdType) {
          // Real data from API - only use if it exists
          allSpend = allByAdType.all_spend || 0;
          allSales = allByAdType.all_sales || 0;
        }

        if (internalByAdType) {
          // Real data from API - only use if it exists
          internalSpend = internalByAdType.internal_spend || 0;
          internalSales = internalByAdType.internal_sales || 0;
          externalSpend = internalByAdType.external_spend || 0;
          externalSales = internalByAdType.external_sales || 0;
        }

        return {
          ...data, // Inherit all budget group properties
          ad_type: record.ad_type,
          allocation_percent: record.allocation_percent,
          budget_group_daily_id: record.budget_group_daily_id,
          isDetailRow: true,
          // Use real or proportional data
          all_spend: allSpend,
          all_sales: allSales,
          internal_spend: internalSpend,
          internal_sales: internalSales,
          external_spend: externalSpend,
          external_sales: externalSales,
        };
      });

    // Detail column definitions - showing AD type-specific data
    const detailColumnDefs = [
      {
        field: 'ad_type',
        headerName: 'AD Type',
        width: 150,
        cellStyle: { paddingLeft: '40px', fontWeight: 'bold' },
      },
      {
        field: 'allocation_percent',
        headerName: 'Allocation %',
        width: 120,
        valueFormatter: (params) => `${params.value}%`,
      },
      {
        field: 'mtd_ppc_spend',
        headerName: 'MTD PPC Spend',
        width: 150,
        valueFormatter: currencyFormatter,
        valueGetter: p => {
          const includes = p.data?.includes || '';
          if (includes === 'limitless') return p.data?.internal_spend || 0;
          return p.data?.all_spend || 0;
        },
      },
      {
        field: 'mtd_ppc_sales',
        headerName: 'MTD PPC Sales',
        width: 150,
        valueFormatter: currencyFormatter,
        valueGetter: p => {
          const includes = p.data?.includes || '';
          if (includes === 'limitless') return p.data?.internal_sales || 0;
          return p.data?.all_sales || 0;
        },
      },
      {
        field: 'mtd_ppc_acos',
        headerName: 'MTD PPC ACoS',
        width: 120,
        valueFormatter: formatDecimalToFixedTwoPercent,
        valueGetter: p => {
          const includes = p.data?.includes || '';
          let spend = 0;
          let sales = 0;
          if (includes === 'limitless') {
            spend = p.data?.internal_spend || 0;
            sales = p.data?.internal_sales || 0;
          } else {
            spend = p.data?.all_spend || 0;
            sales = p.data?.all_sales || 0;
          }
          return sales ? spend / sales : 0;
        },
      },
      {
        field: 'mtd_ppc_roas',
        headerName: 'MTD PPC ROAS',
        width: 120,
        valueFormatter: formatNumberOnePlaceDefaultToZero,
        valueGetter: p => {
          const includes = p.data?.includes || '';
          let spend = 0;
          let sales = 0;
          if (includes === 'limitless') {
            spend = p.data?.internal_spend || 0;
            sales = p.data?.internal_sales || 0;
          } else {
            spend = p.data?.all_spend || 0;
            sales = p.data?.all_sales || 0;
          }
          return spend ? sales / spend : 0;
        },
      },
    ];

    return (
      <div style={{ padding: '10px 20px', backgroundColor: '#f5f5f5' }}>
        <div className={gridTheme} style={{ height: `${(detailRowData.length * 42) + 50}px`, width: '100%' }}>
          <AgGridReact
            theme={gridTheme}
            columnDefs={detailColumnDefs}
            rowData={detailRowData}
            domLayout="autoHeight"
            headerHeight={30}
            rowHeight={42}
            suppressHorizontalScroll={false}
          />
        </div>
      </div>
    );
  }, [gridTheme, allById, internalById]);

  // AG Grid variables
  /* consider these as well (for reference)
    sortable: true,
    comparator: (valueA, valueB) => compareNumber(valueA, valueB),
    resizable: true,
  */
  const columnDefs = [
    {
      field: 'editBg',
      headerName: 'BG',
      editable: false,
      cellRenderer: (params) => {
        if (params.node?.footer) {
          return 'TOTAL/AVG';
        }
        return <button type="submit" onClick={() => changeShowEditModal(params.data)}>Edit Row</button>;
      },
    },
    // {
    //   field: 'editItems',
    //   headerName: 'Items',
    //   editable: false,
    //   cellRenderer: (params) => {
    //     if (params.node?.footer) {
    //       return '';
    //     }
    //     return <button type="submit" onClick={() => changeShowEditModal(params.data)}>Edit Items</button>;
    //   },
    // },
    {
      field: 'budget_group_daily_id',
      headerName: 'Budget Group Daily ID (hide later)',
      editable: false,
      hide: true,
      suppressColumnsToolPanel: !adminView,
    },
    {
      field: 'calcState',
      headerName: 'State',
      editable: false,
      valueGetter: params => {
        let currentState = 'draft';
        if (params.node?.footer) {
          return '';
        }
        if (params.data?.state) {
          currentState = params.data.end_date && moment(params.data.end_date).format('YYYYMMDD') < moment().format('YYYYMMDD') && (params.data.state === 'enabled' || params.data.state === 'disabled') ? 'completed' : params.data.state;
        }
        const dispalyObj = lookupValue(budgetStateOptions, currentState);
        return dispalyObj?.label || '';
      },
    },
    {
      field: 'ad_type_csv',
      headerName: 'AD Type',
      editable: false,
      cellRenderer: (params) => {
        if (params.node?.footer) {
          return '';
        }

        // Get the AD type text
        let adTypeText = '';

        // For non-Amazon platforms, just show ad_type directly from API
        if (retailerConfig?.key !== 'amazon') {
          adTypeText = params.data?.ad_type || params.data?.ad_type_csv || '';
        } else if (params.data?.budget_type?.toLowerCase() === 'dsp') {
          adTypeText = 'DSP';
        } else if (params.data?.budget_type?.toLowerCase() === 'other') {
          // For "Other" budget type (External/Manual ADs), get ad type from ad_type_csv directly
          adTypeText = params.data?.ad_type_csv || '';
        } else {
          // Amazon platform logic - use allocation percentages
          try {
            const budgetJson = JSON.parse(params.data?.budget_json || '{}');
            const percentages = budgetJson?.allocation?.percentages;

            if (percentages) {
              const activeAdTypes = [];
              if (percentages.sp_sd_percent > 0) activeAdTypes.push('SP,SD');
              if (percentages.sb_percent > 0) activeAdTypes.push('SB');
              if (percentages.st_percent > 0) activeAdTypes.push('ST');
              if (percentages.dsp_percent > 0) activeAdTypes.push('DSP');
              adTypeText = activeAdTypes.join(',');
            } else {
              adTypeText = params.data?.ad_type_csv || '';
            }
          } catch (e) {
            adTypeText = params.data?.ad_type_csv || '';
          }
        }

        // If master row (multiple AD types), show arrow before text
        if (isRowMaster(params.data)) {
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  params.node.setExpanded(!params.node.expanded);
                  // Force refresh of this cell to update arrow direction
                  setTimeout(() => {
                    params.api.refreshCells({
                      rowNodes: [params.node],
                      columns: ['ad_type_csv'],
                      force: true,
                    });
                  }, 0);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 5px 0 0',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#666',
                }}
              >
                {params.node.expanded ? 'v' : '>'}
              </button>
              <span>{adTypeText}</span>
            </div>
          );
        }

        // For single AD type rows, just show the text
        return adTypeText;
      },
    },
    {
      field: 'allocation_sp_sd',
      headerName: 'SP,SD%',
      editable: false,
      hide: !hideIndividualAllocations || retailerConfig?.key !== 'amazon',
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;
          if (percentages && typeof percentages.sp_sd_percent !== 'undefined') {
            return percentages.sp_sd_percent;
          }
          // Default to 100% for SP,SD if no allocation data
          return 100;
        } catch (e) {
          return 100;
        }
      },
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return params.value !== null && params.value !== undefined ? `${params.value}%` : '0%';
      },
    },
    {
      field: 'allocation_sb',
      headerName: 'SB%',
      editable: false,
      hide: !hideIndividualAllocations || retailerConfig?.key !== 'amazon',
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;
          return percentages?.sb_percent || 0;
        } catch (e) {
          return 0;
        }
      },
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return params.value !== null && params.value !== undefined ? `${params.value}%` : '0%';
      },
    },
    {
      field: 'allocation_st',
      headerName: 'ST%',
      editable: false,
      hide: !hideIndividualAllocations || retailerConfig?.key !== 'amazon',
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;
          return percentages?.st_percent || 0;
        } catch (e) {
          return 0;
        }
      },
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return params.value !== null && params.value !== undefined ? `${params.value}%` : '0%';
      },
    },
    {
      field: 'allocation_dsp',
      headerName: 'DSP%',
      editable: false,
      hide: !hideIndividualAllocations || retailerConfig?.key !== 'amazon',
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;
          return percentages?.dsp_percent || 0;
        } catch (e) {
          return 0;
        }
      },
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return params.value !== null && params.value !== undefined ? `${params.value}%` : '0%';
      },
    },
    {
      field: 'budget_group_name',
      headerName: 'Budget Group',
      editable: false,
    },
    {
      field: 'budget_type',
      headerName: 'Budget Type',
      editable: false,
      valueFormatter: (params) => {
        if (params.value) {
          const lowerValue = params.value?.toLowerCase();
          // DSP should always be uppercase
          if (lowerValue === 'dsp') {
            return 'DSP';
          }
          // Other should be capitalized
          if (lowerValue === 'other') {
            return 'Other';
          }
          // Default: capitalize first letter
          return params.value.charAt(0).toUpperCase() + params.value.slice(1);
        }
        return params.value;
      },
    },
    {
      headerName: 'Budgets',
      children: [
        {
          field: 'start_date',
          headerName: 'Start',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return params.value;
          },
        },
        {
          field: 'end_date',
          headerName: 'End',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return params.value;
          },
        },
        {
          field: 'budget_driver',
          headerName: 'Driver',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            if (params.value) {
              return params.value.charAt(0).toUpperCase() + params.value.slice(1);
            }
            return params.value;
          },
        },
        {
          field: 'calcDaily',
          headerName: 'Daily',
          aggFunc: 'sum',
          valueGetter: params => {
            let dailyBudget = 0;
            if (params.data?.budget_driver === 'daily') {
              const budgetJson = JSON.parse(params.data.budget_json || '{}');
              // Try allocation.original.daily.budget first, fallback to daily.budget for backwards compatibility
              if (budgetJson.allocation?.original?.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.allocation.original.daily.budget);
              } else if (budgetJson.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.daily.budget);
              }
            } else if (params.data?.budget_driver === 'monthly') {
              const activeDaysInMonth = countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
              if (activeDaysInMonth > 0) {
                const budgetJson = JSON.parse(params.data.budget_json || '{}');
                const monthKey = moment().format('M').toString();
                const monthName = budgetJson.monthly?.[monthKey]?.name?.toLowerCase();
                // Try allocation.original.monthly[monthName] first, fallback to monthly[monthKey].budget for backwards compatibility
                let monthBudget = 0;
                if (monthName && budgetJson.allocation?.original?.monthly?.[monthName] !== undefined) {
                  monthBudget = parseFloat(budgetJson.allocation.original.monthly[monthName]);
                } else if (budgetJson.monthly?.[monthKey]?.budget !== undefined) {
                  monthBudget = parseFloat(budgetJson.monthly[monthKey].budget);
                }
                dailyBudget = monthBudget / activeDaysInMonth;
              }
            }
            return dailyBudget;
          },
          valueFormatter: (params) => currencyFormatter(params),
        },
        {
          field: 'calcMonthly',
          headerName: 'Monthly',
          aggFunc: 'sum',
          valueGetter: (params) => {
            let currentMonthBudget = 0;
            if (params.data?.budget_driver === 'daily') {
              const activeDaysInMonth = countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
              const budgetJson = JSON.parse(params.data.budget_json || '{}');
              // Try allocation.original.daily.budget first, fallback to daily.budget for backwards compatibility
              let dailyBudget = 0;
              if (budgetJson.allocation?.original?.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.allocation.original.daily.budget);
              } else if (budgetJson.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.daily.budget);
              }
              currentMonthBudget = dailyBudget * activeDaysInMonth;
            } else if (params.data?.budget_driver === 'monthly') {
              const budgetJson = JSON.parse(params.data.budget_json || '{}');
              const monthKey = moment().format('M').toString();
              const monthName = budgetJson.monthly?.[monthKey]?.name?.toLowerCase();
              // Try allocation.original.monthly[monthName] first, fallback to monthly[monthKey].budget for backwards compatibility
              if (monthName && budgetJson.allocation?.original?.monthly?.[monthName] !== undefined) {
                currentMonthBudget = parseFloat(budgetJson.allocation.original.monthly[monthName]);
              } else if (budgetJson.monthly?.[monthKey]?.budget !== undefined) {
                currentMonthBudget = parseFloat(budgetJson.monthly[monthKey].budget);
              }
            }
            return currentMonthBudget;
          },
          valueFormatter: (params) => currencyFormatter(params),
        },
        {
          field: 'calcActiveDays',
          headerName: 'Active Days',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.active_days_csv) {
              return readableActiveDays(params.data.active_days_csv);
            }
            return 0;
          },
        },
        {
          field: 'calcActiveDaysInWeek',
          headerName: 'Active Days this Week',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.active_days_csv) {
              const matches = params.data.active_days_csv.match(/1/g);
              return matches ? matches.length : 0;
            }
            return 0;
          },
        },
        {
          field: 'calcActiveDaysInMonth',
          headerName: 'Active Days this Month',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.active_days_csv) {
              return countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
            }
            return 0;
          },
        },
        {
          field: 'calcDaysInMonth',
          headerName: 'Days this Month',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            return moment().daysInMonth()
          },
        },
        {
          field: 'active_events',
          headerName: 'Active Events',
          hide: !hideNonEssentialBudget,

        },
        {
          field: 'upcoming_events',
          headerName: 'Upcoming Events',
          hide: !hideNonEssentialBudget,
        },
        {
          field: 'past_events',
          headerName: 'Past Events',
          hide: !hideNonEssentialBudget,
        },
        {
          field: 'reallocation',
          headerName: 'Reallocation',
          hide: true,
          suppressColumnsToolPanel: !adminView,
          aggFunc: 'sum',
          valueFormatter: params => currencyFormatter(params),
          valueGetter: (params) => {
            try {
              const budgetJson = JSON.parse(params.data?.budget_json || '{}');
              return parseFloat(budgetJson.daily?.reallocation || 0);
            } catch (e) {
              return 0;
            }
          },
        },
      ],
    },
    {
      headerName: 'Cost Targets',
      children: [
        {
          field: 'cost_target_type',
          headerName: 'Cost Target Type',
          valueFormatter: (params) => {
            if (params.value === 'bnb') {
              return 'BNB';
            }
            if (params.value === 'blended') {
              return 'Blended';
            }
            return params.value;
          },
        },
        {
          field: 'cost_target_brand',
          headerName: 'Brand Cost Target',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.cost_target_metric === 'pacos') {
              return params.value.toString().concat('% PACoS');
            }
            if (params.data?.cost_target_metric === 'roas') {
              return params.value.toString().concat(' ROAS');
            }
            return params.value;
          },
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            return (params.data?.cost_target_brand || 0);
          },
        },
        {
          field: 'cost_target_nonbrand',
          headerName: 'Non-Brand Cost Target',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.cost_target_metric === 'pacos') {
              return params.value.toString().concat('% PACoS');
            }
            if (params.data?.cost_target_metric === 'roas') {
              return params.value.toString().concat(' ROAS');
            }
            return params.value;
          },
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            return (params.data?.cost_target_nonbrand || 0);
          },
        },
      ],
    },
    {
      headerName: 'Spend Controls',
      children: [
        {
          field: 'portfolio_ids_csv',
          headerName: 'Portfolio ID',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => (params.data?.portfolio_ids_csv || '').replace(/[^\-0-9.,]/g, ''),
        },
        {
          field: 'limitless_budget',
          headerName: 'Budget Cap Type',
          hide: !adminView,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(limitlessBudgetOptions, params.data?.limitless_budget || 0);
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'spend_priority',
          headerName: 'Spend Priority',
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(spendPriorityOptions, params.data?.spend_priority || '0');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'spend_tactic',
          headerName: 'Spend Tactic',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(spendTacticOptions, params.data?.spend_tactic || 'max_perf');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'spend_speed',
          headerName: 'Spend Speed',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(spendSpeedOptions, params.data?.spend_speed || 'speed_level_1');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'item_type',
          headerName: 'Item Type',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(itemTypeOptions, params.data?.item_type || '');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'item_selection',
          headerName: 'Item Selection',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(itemSelectionOptions, params.data?.item_selection || 'ALL');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'destination',
          headerName: 'Destination',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(destinationOptions, params.data?.destination || 'ON');
            return dispalyObj?.label || '';
          },
        },
      ],
    },
    {
      headerName: 'Metrics',
      children: [
        {
          field: 'includes',
          headerName: 'Data Filter',
          hide: !hideNonEssentialMetrics,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            // For DSP budget type, includes field stores gender targeting, not data filter
            const isDspBudgetType = params.data?.budget_type?.toLowerCase() === 'dsp';

            if (isDspBudgetType) {
              // Use genderMap for DSP budget type
              const genderValue = params.data?.includes || 'male_and_female';
              return genderMap[genderValue] || genderValue;
            }

            // For non-DSP budgets (including Other type with DSP ad type), use normal includes options
            const dispalyObj = lookupValue(includesOptions, params.data?.includes || '');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'mtd_ppc_spend',
          headerName: 'MTD PPC Spend',
          hide: !hideNonEssentialMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
          valueGetter: p => {
            const includes = p.data?.includes || '';
            if (includes === 'limitless') return p.data?.internal_spend || 0;
            return p.data?.all_spend || 0;
          },
        },
        {
          field: 'mtd_ppc_sales',
          headerName: 'MTD PPC Sales',
          hide: !hideNonEssentialMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
          valueGetter: p => {
            const includes = p.data?.includes || '';
            if (includes === 'limitless') return p.data?.internal_sales || 0;
            return p.data?.all_sales || 0;
          },
        },
        {
          field: 'mtd_ppc_acos',
          headerName: 'MTD PPC ACoS',
          hide: !hideNonEssentialMetrics,
          editable: false,
          // aggregate from the column sums (spend ÷ sales)
          aggFunc: params => {
            const spend = params?.rowNode?.aggData?.mtd_ppc_spend ?? 0;
            const sales = params?.rowNode?.aggData?.mtd_ppc_sales ?? 0;
            return sales ? spend / sales : 0;
          },
          valueFormatter: formatDecimalToFixedTwoPercent,
          valueGetter: p => {
            const includes = p.data?.includes || '';
            let spend = 0;
            let sales = 0;
            if (includes === 'limitless') {
              spend = p.data?.internal_spend || 0;
              sales = p.data?.internal_sales || 0;
            } else {
              spend = p.data?.all_spend || 0;
              sales = p.data?.all_sales || 0;
            }
            return sales ? spend / sales : 0;
          },
          headerTooltip: 'MTD PPC ACoS',
        },
        {
          field: 'mtd_ppc_roas',
          headerName: 'MTD PPC ROAS',
          hide: !hideNonEssentialMetrics,
          editable: false,
          aggFunc: params => {
            const spend = params?.rowNode?.aggData?.mtd_ppc_spend ?? 0;
            const sales = params?.rowNode?.aggData?.mtd_ppc_sales ?? 0;
            return spend ? sales / spend : 0;
          },
          valueFormatter: formatNumberOnePlaceDefaultToZero,
          valueGetter: p => {
            const includes = p.data?.includes || '';
            let spend = 0;
            let sales = 0;
            if (includes === 'limitless') {
              spend = p.data?.internal_spend || 0;
              sales = p.data?.internal_sales || 0;
            } else {
              spend = p.data?.all_spend || 0;
              sales = p.data?.all_sales || 0;
            }
            return spend ? sales / spend : 0;
          },
          headerTooltip: 'MTD PPC ROAS',
        },
        // {
        //   field: 'mtd_orders',
        //   headerName: 'MTD Orders',
        //   hide: true,
        //   editable: false,
        //   aggFunc: 'sum',
        //   valueFormatter: formatNumberDefaultToZero,
        //   valueGetter: function calcValueGetter(params) {
        //     const isInternal = params.data?.includes !== '';
        //     const order = isInternal ? params.data?.internal_orders : params.data?.all_orders;
        //     return order;
        //   },
        // },
        {
          field: '',
          headerName: 'MTD Consumption Rate',
          hide: !hideNonEssentialMetrics,
          editable: false,
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return formatDecimalToFixedTwoPercent(params);
          },
          valueGetter: function calcValueGetter(params) {
            if (params.node?.footer) {
              return '';
            }
            let totalBudget = 0;
            if (params.data?.budget_driver === 'daily') {
              const activeDaysInMonth = countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
              const budgetJson = JSON.parse(params.data.budget_json || '{}');
              // Try allocation.original.daily.budget first, fallback to daily.budget for backwards compatibility
              let dailyBudget = 0;
              if (budgetJson.allocation?.original?.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.allocation.original.daily.budget);
              } else if (budgetJson.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.daily.budget);
              }
              totalBudget = dailyBudget * activeDaysInMonth;
            } else if (params.data?.budget_driver === 'monthly') {
              const budgetJson = JSON.parse(params.data.budget_json || '{}');
              const monthKey = moment().format('M').toString();
              const monthName = budgetJson.monthly?.[monthKey]?.name?.toLowerCase();
              // Try allocation.original.monthly[monthName] first, fallback to monthly[monthKey].budget for backwards compatibility
              if (monthName && budgetJson.allocation?.original?.monthly?.[monthName] !== undefined) {
                totalBudget = parseFloat(budgetJson.allocation.original.monthly[monthName]);
              } else if (budgetJson.monthly?.[monthKey]?.budget !== undefined) {
                totalBudget = parseFloat(budgetJson.monthly[monthKey].budget);
              }
            }

            let calcBudget = totalBudget; // limitless_budget === 0
            const daysInMonth = moment().daysInMonth();
            if (params.data.limitless_budget === 1) {
              calcBudget = totalBudget * 10;
            }
            if (params.data.limitless_budget === 2) {
              calcBudget = totalBudget * 1.25;
            }

            const includes = params.data?.includes || '';
            let spend = 0;
            let reportDaysCount = reportDays.allReportDays;

            if (includes === 'limitless') {
              spend = params.data.internal_spend || 0;
              reportDaysCount = reportDays.internalReportDays;
            } else {
              spend = params.data.all_spend || 0;
              reportDaysCount = reportDays.allReportDays;
            }

            if (params && params.data && spend && reportDaysCount !== 0 && calcBudget && calcBudget !== 0 && daysInMonth !== 0) {
              return (spend / reportDaysCount) / (calcBudget / daysInMonth);
            }
            return 0;
          },
          headerTooltip: 'Consumption',
        },
        {
          field: 'itemOnCount',
          headerName: 'Items ON in BG',
          hide: !hideNonEssentialMetrics,
          editable: false,
          aggFunc: 'sum',
        },
        // {
        //   field: 'calcBgDailyDollarPerItem',
        //   headerName: 'BG Daily $/Item',
        //   hide: !hideNonEssentialMetrics,
        //   editable: false,
        //   valueFormatter: (params) => {
        //     if (params.node?.footer) {
        //       return '';
        //     }
        //     return currencyFormatter(params);
        //   },
        //   valueGetter: function calcValueGetter(params) {
        //     const daysInMonth = moment().daysInMonth();
        //     if (params && params.data && params.data.itemOnCount && params.data.itemOnCount !== 0 && daysInMonth !== 0) {
        //       const totalBudget = (params.data.current || 0) + (params.data.reallocation || 0) + (params.data.carryover || 0) + (params.data.supplemental || 0);
        //       return (totalBudget / daysInMonth) / params.data.itemOnCount;
        //     }
        //     return 0;
        //   },
        // },
        {
          field: 'calcMtdDailyDollarPerItem',
          headerName: 'MTD Daily $/Item',
          hide: !hideNonEssentialMetrics,
          editable: false,
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return currencyFormatter(params);
          },
          valueGetter: function calcValueGetter(params) {
            const includes = params.data?.includes || '';
            let spend = 0;
            if (includes === 'limitless') {
              spend = params.data?.internal_spend || 0;
            } else {
              spend = params.data?.all_spend || 0;
            }
            if (params && params.data && spend && params.data.itemOnCount && params.data.itemOnCount !== 0 && reportDays.internalReportDays !== 0) {
              return (spend / reportDays.internalReportDays) / params.data.itemOnCount;
            }
            return 0;
          },
        },
      ],
    },
    {
      headerName: 'Advanced Metrics',
      suppressColumnsToolPanel: !adminView,
      children: [
        {
          field: 'internal_advertised_sku_sales',
          headerName: 'MTD Limitless Advertised Sku Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_brand_sales',
          headerName: 'MTD Limitless Brand Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_direct_sales',
          headerName: 'MTD Limitless Direct Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_related_sales',
          headerName: 'MTD Limitless Related Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_ntb_orders',
          headerName: 'MTD Limitless NTB Orders',
          hide: !adminView,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'internal_ntb_revenue',
          headerName: 'MTD Limitless NTB Revenue',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_other_sku_sales',
          headerName: 'MTD Limitless Other SKU Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_in_store_orders',
          headerName: 'MTD Limitless In Store Orders',
          hide: !adminView,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'internal_in_store_sales',
          headerName: 'MTD Limitless In Store Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_advertised_sku_sales',
          headerName: 'MTD ALL Advertised Sku Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_brand_sales',
          headerName: 'MTD ALL Brand Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_direct_sales',
          headerName: 'MTD ALL Direct Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_related_sales',
          headerName: 'MTD ALL Related Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_ntb_orders',
          headerName: 'MTD ALL NTB Orders',
          hide: true,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'all_ntb_revenue',
          headerName: 'MTD ALL NTB Revenue',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_other_sku_sales',
          headerName: 'MTD ALL Other SKU Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_in_store_orders',
          headerName: 'MTD ALL In Store Orders',
          hide: true,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'all_in_store_sales',
          headerName: 'MTD ALL In Store Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
      ],
    },
    {
      headerName: 'Sparklines',
      children: [
        {
          field: 'spendChange',
          headerName: 'Spend Change',
          hide: !hideNonEssentialSparklines,
          cellRenderer: 'agSparklineCellRenderer',
          valueGetter: params => {
            const includes = params.data?.includes || '';
            return includes === 'limitless'
              ? params.data?.internalSpendChange
              : params.data?.allSpendChange;
          },
          cellRendererParams: (params) => createSparklineCellRendererParams(params, { dataField: 'SpendChange' }),
        },
        {
          field: 'salesChange',
          headerName: 'Sales Change',
          hide: !hideNonEssentialSparklines,
          cellRenderer: 'agSparklineCellRenderer',
          valueGetter: params => {
            const includes = params.data?.includes || '';
            return includes === 'limitless'
              ? params.data?.internalSalesChange
              : params.data?.allSalesChange;
          },
          cellRendererParams: (params) => createSparklineCellRendererParams(params, { dataField: 'SalesChange' }),
        },
        {
          field: 'acosChange',
          headerName: 'ACoS Change',
          hide: !hideNonEssentialSparklines,
          cellRenderer: 'agSparklineCellRenderer',
          valueGetter: params => {
            const includes = params.data?.includes || '';
            return includes === 'limitless'
              ? params.data?.internalAcosChange
              : params.data?.allAcosChange;
          },
          cellRendererParams: (params) => createSparklineCellRendererParams(params, { dataField: 'AcosChange' }),
        },
      ],
    },
  ];

  const onBtnExport = () => {
    const exportPrefix = getExportPrefix(`${retailerConfig.prefix}-daily-budget-groups`, sellerSelect, true);
    const fileName = `${exportPrefix}.csv`;
    gridRef.current.api.exportDataAsCsv({ fileName });
  };

  const defaultColDef = {
    editable: false,
    sortable: true,
    filter: true,
    resizable: true,
  }

  const isDarkMode = theme.className && !theme.className.endsWith('light');

  return (
    <>
      <div style={{ height: '100%', width: '100%' }}>
        <GroupEditModal
          showEditModal={showEditModal}
          currentEditRow={editRowData}
          changeShowEditModal={changeShowEditModal}
          setForceRefresh={setForceRefresh}
          budgetAccountData={budgetAccountData}
          activeDayOptions={activeDayOptions}
          disableButton={disableButton}
          setDisableButton={setDisableButton}
          setShowEditModal={setShowEditModal}
          retailerConfig={retailerConfig}
        />
        <Panel
          lg={12}
          md={12}
          title={`${title} Group Settings`}
          subhead="Used to manage budgets at a 'budget group' level"
          parentRefresh={panelLoad || allGraphLoad || internalGraphLoad}
          getData={() => getData()}
        >
          <div
            className={gridTheme}
            style={{ width: '100%' }}
          >
            <br />
            <div className="topbar__wrapper">
              <div style={{ color: isDarkMode ? '#dddddd' : '#212529' }}>
                Show Additional Columns:
                <ToggleWrapOuter>
                  <ToggleSlide
                    name="Budgets"
                    setFunction={() => setHideNonEssentialBudget(!hideNonEssentialBudget)}
                    currentState={hideNonEssentialBudget}
                    bgColor={platfromSpec.colorPrimary}
                    isDarkMode={isDarkMode}
                  />
                  <ToggleSlide
                    name="Individual Allocations"
                    setFunction={() => setHideIndividualAllocations(!hideIndividualAllocations)}
                    currentState={hideIndividualAllocations}
                    bgColor={platfromSpec.colorPrimary}
                    isDarkMode={isDarkMode}
                  />
                  {user && user.access === 'admin' && adminView && (
                    <ToggleSlide
                      name="Spend Controls"
                      setFunction={() => setHideNonEssentialSpendControls(!hideNonEssentialSpendControls)}
                      currentState={hideNonEssentialSpendControls}
                      bgColor={platfromSpec.colorPrimary}
                      isDarkMode={isDarkMode}
                    />
                  )}
                  <ToggleSlide
                    name="Metrics"
                    setFunction={() => setHideNonEssentialMetrics(!hideNonEssentialMetrics)}
                    currentState={hideNonEssentialMetrics}
                    bgColor={platfromSpec.colorPrimary}
                    isDarkMode={isDarkMode}
                  />
                  {user && user.access === 'admin' && adminView && (
                    <ToggleSlide
                      name="Advanced Metrics"
                      setFunction={() => setHideNonEssentialAdvancedMetrics(!hideNonEssentialAdvancedMetrics)}
                      currentState={hideNonEssentialAdvancedMetrics}
                      bgColor={platfromSpec.colorPrimary}
                      isDarkMode={isDarkMode}
                    />
                  )}
                  <ToggleSlide
                    name="Sparklines"
                    setFunction={() => setHideNonEssentialSparklines(!hideNonEssentialSparklines)}
                    currentState={hideNonEssentialSparklines}
                    bgColor={platfromSpec.colorPrimary}
                    isDarkMode={isDarkMode}
                  />
                  {user && user.access === 'admin' && (
                    <ToggleSlide
                      name="AdminView"
                      setFunction={() => setAdminView(!adminView)}
                      currentState={adminView}
                      bgColor={platfromSpec.colorPrimary}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </ToggleWrapOuter>
              </div>
              <div className="topbar__right">
                <div className="form__button-toolbar">
                  <button className="btn btn-primary" onClick={() => changeShowEditModal(addNewDefault)} type="button">Add Budget Group</button>
                  <button className="btn btn-primary" onClick={onBtnExport} type="button">Export</button>
                </div>
              </div>
            </div>
            <br />
            <div className={gridTheme} style={{ height: '50vh', width: '100%' }}>
              <AgGridReact
                theme={gridTheme}
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={combinedItems}
                defaultColDef={defaultColDef}
                grandTotalRow="pinnedBottom"
                aggFuncs={aggFuncs}
                onGridReady={onGridReady}
                suppressScrollOnNewData
                enableRangeSelection
                rowSelection="multiple"
                masterDetail
                isRowMaster={isRowMaster}
                detailCellRenderer={detailCellRenderer}
                detailRowAutoHeight
              />
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}

BudgetTable.propTypes = {
  gridTheme: PropTypes.string,
  internalGraphLoad: PropTypes.bool.isRequired,
  allGraphLoad: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  retailer: PropTypes.string.isRequired,
  internalById: PropTypes.shape().isRequired,
  allById: PropTypes.shape().isRequired,
};

BudgetTable.defaultProps = {
  gridTheme: themeQuartz.withPart(colorSchemeLight),
};
